export interface SpinCasinoAnimationOptions {
  spinItems: HTMLElement[];
  winningIndex: number;
  minCycles?: number;
  maxCycles?: number;
  speedStart?: number;
  speedEnd?: number;
  onComplete: (winningElement: HTMLElement | null) => void;
}

export function removeAllSpinHighlights(spinItems: HTMLElement[]): void {
  spinItems.forEach((item) => item.classList.remove('spin-item-highlighted'));
}

export function addSpinWinningCheck(itemElement: HTMLElement | null): void {
  if (!itemElement) return;
  const existing = itemElement.querySelector('.spin-item-claimed-overlay');
  if (existing) existing.remove();

  const checkOverlay = document.createElement('div');
  checkOverlay.className = 'spin-item-claimed-overlay';
  checkOverlay.innerHTML = '<i class="ph-light ph-check-circle"></i>';
  itemElement.style.position = 'relative';
  itemElement.appendChild(checkOverlay);
}

export function removeSpinWinningCheck(itemElement: HTMLElement | null): void {
  if (!itemElement) return;
  const checkOverlay = itemElement.querySelector('.spin-item-claimed-overlay');
  if (checkOverlay) checkOverlay.remove();
}

function highlightSpinItem(spinItems: HTMLElement[], index: number): void {
  removeAllSpinHighlights(spinItems);
  if (index >= 0 && index < spinItems.length) {
    spinItems[index].classList.add('spin-item-highlighted');
  }
}

export function runSpinCasinoAnimation(options: SpinCasinoAnimationOptions): () => void {
  const {
    spinItems,
    winningIndex,
    minCycles = 3,
    maxCycles = 5,
    speedStart = 80,
    speedEnd = 200,
    onComplete,
  } = options;

  if (spinItems.length === 0) {
    onComplete(null);
    return () => {};
  }

  const totalCycles = Math.floor(Math.random() * (maxCycles - minCycles + 1)) + minCycles;
  let currentItemIndex = 0;
  let currentSpeed = speedStart;
  let itemsHighlighted = 0;
  let highlightInterval: ReturnType<typeof setInterval> | null = null;
  let finishTimeout: ReturnType<typeof setTimeout> | null = null;

  const clearTimers = () => {
    if (highlightInterval) clearInterval(highlightInterval);
    if (finishTimeout) clearTimeout(finishTimeout);
    highlightInterval = null;
    finishTimeout = null;
  };

  const highlightNext = () => {
    if (itemsHighlighted >= totalCycles * spinItems.length) {
      highlightSpinItem(spinItems, winningIndex);
      const winningElement = spinItems[winningIndex] ?? null;
      finishTimeout = setTimeout(() => {
        clearTimers();
        removeAllSpinHighlights(spinItems);
        addSpinWinningCheck(winningElement);
        onComplete(winningElement);
      }, 800);
      return;
    }

    highlightSpinItem(spinItems, currentItemIndex);
    currentItemIndex = (currentItemIndex + 1) % spinItems.length;
    itemsHighlighted += 1;

    const progress = itemsHighlighted / (totalCycles * spinItems.length);
    if (progress < 0.3) {
      currentSpeed = speedStart;
    } else if (progress < 0.7) {
      currentSpeed = speedStart + (speedEnd - speedStart) * 0.4;
    } else {
      currentSpeed = speedStart + (speedEnd - speedStart) * 0.7;
    }

    if (highlightInterval) clearInterval(highlightInterval);
    highlightInterval = setInterval(highlightNext, currentSpeed);
  };

  highlightInterval = setInterval(highlightNext, currentSpeed);

  return clearTimers;
}

export function formatSpinTimeAgo(rewardedAt: string): string {
  const now = Date.now();
  const rewarded = new Date(rewardedAt).getTime();
  const diff = Math.max(0, Math.floor((now - rewarded) / 1000));

  if (diff < 60) return `hace ${diff} seg`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `hace ${hours} hora${hours === 1 ? '' : 's'}`;
  }
  const days = Math.floor(diff / 86400);
  return `hace ${days} día${days === 1 ? '' : 's'}`;
}
